// routesConfig.js (optional, for clean structure)
import React from "react";
import { Navigate } from "react-router-dom";
import DetailsSkeleton from "../ui/details-header/DetailsSkeleton";
const ForgotPass = React.lazy(() => import("../modules/auth/ForgotPass"));
const Login = React.lazy(() => import("../modules/auth/Login"));
const NewPassword = React.lazy(() => import("../modules/auth/NewPassword"));
const OtpVerify = React.lazy(() => import("../modules/auth/OtpVerify"));
const CustomerModule = React.lazy(() => import("../modules/customer"));
// Details pages: while the page's code downloads (e.g. on a browser reload), show the same
// skeleton the page shows while its data loads, inside the normal sidebar + header, instead of
// the full-screen spinner. skeleton = DetailsSkeleton props (tracking has its own layout).
const withSkeleton = (element, skeleton = {}) => (
  <React.Suspense fallback={<DetailsSkeleton {...skeleton} />}>{element}</React.Suspense>
);
const TRACKING_SKELETON = {
  layoutClassName: "",
  map: true,
  right: false,
  height: "calc(100vh - 68px)",
};

const CustomerDetails = React.lazy(() => import("../modules/customer/details/CustomerDetails"));
const DashboardPage = React.lazy(() => import("../modules/dashboard/DashboardPage"));
const FollowUpModule = React.lazy(() => import("../modules/follow-up"));
const FollowUpDetails = React.lazy(() => import("../modules/follow-up/details/FollowUpDetails"));
const LeadModule = React.lazy(() => import("../modules/lead"));
const LeadDetails = React.lazy(() => import("../modules/lead/details/LeadDetails"));
const LeadForm = React.lazy(() => import("../modules/lead/LeadForm"));
const ProductModule = React.lazy(() => import("../modules/products"));
const ProductDetails = React.lazy(() => import("../modules/products/details/ProductDetails"));
const ProductSettings = React.lazy(() => import("../modules/products/product-settings"));
const ProductPackagesModule = React.lazy(() => import("../modules/products/packages"));
const ProductPackagesForm = React.lazy(() => import("../modules/products/packages/PackagesForm"));
const ProductForm = React.lazy(() => import("../modules/products/ProductForm"));
const QuotationModule = React.lazy(() => import("../modules/quotation"));

const QuotationForm = React.lazy(() => import("../modules/quotation/QuotationForm"));
const ProfilePage = React.lazy(() => import("../modules/profile"));
const NotificationsPage = React.lazy(() => import("../modules/notifications"));

// import Test from "../modules/settings/Test";
// no-comment
const UserRolesPage = React.lazy(() => import("../modules/user-roles"));
const PermissionPage = React.lazy(() => import("../modules/user-roles/permissions/PermissionPage"));
const RolesList = React.lazy(() => import("../modules/user-roles/RolesList"));
const SalesTargetList = React.lazy(() => import("../modules/user-roles/SalesTargetList"));
const QuotationDetails = React.lazy(() => import("../modules/quotation/details/QuotationDetails"));
const TrackingModule = React.lazy(() => import("../modules/tracking"));
const TrackingDetails = React.lazy(() => import("../modules/tracking/details/TrackingDetails"));
const MonitoringDetails = React.lazy(() => import("../modules/monitoring/MonitoringDetails"));
const MonitoringList = React.lazy(() => import("../modules/monitoring/MonitoringList"));
const MonitoringModuleContainer = React.lazy(
  () => import("../modules/monitoring/MonitoringModuleContainer")
);
// import App from "../App";

const SettingsModule = React.lazy(() => import("../modules/settings"));
const ReportsModule = React.lazy(() => import("../modules/reports"));

export const authRoutes = [
  { path: "/login", element: <Login /> },
  { path: "/forgot-password", element: <ForgotPass /> },
  { path: "/verification", element: <OtpVerify /> },
  { path: "/new-password", element: <NewPassword /> },
];

export const protectedRoutes = [
  { path: "/", element: <DashboardPage /> },
  { path: "/dashboard/leads", element: <DashboardPage view="leads" />, module: "leads" },
  { path: "/dashboard/sales", element: <DashboardPage view="sales" />, module: "quotations" },
  { path: "/dashboard/follow-ups", element: <DashboardPage view="follow-ups" />, module: "followups" },
  { path: "/settings", element: <SettingsModule />, module: "settings" },
  { path: "/reports", element: <ReportsModule /> },
  { path: "/quotation", element: <QuotationModule />, module: "quotations" },
  { path: "/quotation/create", element: <QuotationForm />, module: "quotations", action: "create" },
  { path: "/quotation/edit/:id", element: <QuotationForm />, module: "quotations", action: "edit" },
  {
    path: "/quotation/revision/:id",
    element: <QuotationForm />,
    module: "quotations",
    action: "create",
  },
  { path: "/quotation/:id", element: withSkeleton(<QuotationDetails />), module: "quotations" },
  { path: "/lead", element: <LeadModule />, module: "leads" },
  { path: "/lead/create", element: <LeadForm />, module: "leads", action: "create" },
  { path: "/lead/edit/:id", element: <LeadForm />, module: "leads", action: "edit" },
  { path: "/lead/:id", element: withSkeleton(<LeadDetails />), module: "leads" },
  { path: "/customer", element: <CustomerModule />, module: "customers" },
  { path: "/customer/:id", element: withSkeleton(<CustomerDetails />), module: "customers" },
  { path: "/products", element: <ProductModule />, module: "products" },
  { path: "/products/create", element: <ProductForm />, module: "products", action: "create" },
  { path: "/products/product-settings", element: <ProductSettings />, module: "products" },
  { path: "/products/edit/:id", element: <ProductForm />, module: "products", action: "edit" },
  { path: "/products/:id", element: withSkeleton(<ProductDetails />), module: "products" },
  { path: "/products/packages", element: <ProductPackagesModule />, module: "products" },
  {
    path: "/products/packages/create",
    element: <ProductPackagesForm />,
    module: "products",
    action: "create",
  },
  {
    path: "/products/packages/edit/:id",
    element: <ProductPackagesForm />,
    module: "products",
    action: "edit",
  },
  { path: "/user-roles", element: <UserRolesPage />, module: "userprofile" },
  { path: "/user-roles/permissions", element: <PermissionPage />, module: "userprofile" },
  { path: "/user-roles/roles", element: <RolesList />, module: "userprofile" },
  { path: "/user-roles/sales-targets", element: <SalesTargetList />, module: "reports" },
  { path: "/follow-ups", element: <FollowUpModule />, module: "followups" },
  { path: "/follow-ups/:id", element: withSkeleton(<FollowUpDetails />), module: "followups" },
  { path: "/reports", element: <Navigate to="/reports/overview" replace />, module: "reports" },
  { path: "/reports/overview", element: <ReportsModule tab="overview" />, module: "reports" },
  { path: "/reports/leads", element: <ReportsModule tab="leads" />, module: "reports" },
  { path: "/reports/quotations", element: <ReportsModule tab="quotations" />, module: "reports" },
  { path: "/reports/followups", element: <ReportsModule tab="followups" />, module: "reports" },
  { path: "/reports/team", element: <ReportsModule tab="team" />, module: "reports" },
  { path: "/reports/scorecard", element: <ReportsModule tab="team" />, module: "reports" },
  { path: "/profile", element: <ProfilePage /> },
  { path: "/notifications", element: <NotificationsPage /> },
  { path: "/tracking", element: <TrackingModule />, module: "tracking" },
  { path: "/tracking/:id", element: withSkeleton(<TrackingDetails />, TRACKING_SKELETON), module: "tracking" },
  { path: "/monitoring", element: <MonitoringList />, module: "monitoring" },
  { path: "/monitoring/:id", element: <MonitoringDetails />, module: "monitoring" },
  { path: "/monitoring/old/:moduleId", element: <MonitoringModuleContainer /> },
  // { path: "/test", element: <App />,module: "tracking" },
];
